import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { revalidateSession } from '../actions/hub/hubAuthPagesActions';
import { AUTHENTICATION_STATUS } from '../util/constants';

const REVALIDATE_DEBOUNCE_MS = 2000;

/**
 * When the hub tab becomes visible again (multi-tab student-hub logout, or
 * bfcache restore), force-refresh Cognito so a GlobalSignOut elsewhere
 * invalidates this session promptly.
 */
export function useRevalidateAuthOnVisible() {
  const dispatch = useDispatch();
  // @ts-expect-error login slice is untyped JS
  const authenticationStatus = useSelector((state) => state.login.authenticationStatus);
  const inFlightRef = useRef(false);
  const lastCheckRef = useRef(0);

  useEffect(() => {
    if (authenticationStatus !== AUTHENTICATION_STATUS.AUTHENTICATED) {
      return undefined;
    }

    const revalidate = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }

      const now = Date.now();
      if (inFlightRef.current || now - lastCheckRef.current < REVALIDATE_DEBOUNCE_MS) {
        return;
      }

      inFlightRef.current = true;
      lastCheckRef.current = now;

      Promise.resolve(dispatch(revalidateSession())).finally(() => {
        inFlightRef.current = false;
      });
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        revalidate();
      }
    };

    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted || document.visibilityState === 'visible') {
        revalidate();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [authenticationStatus, dispatch]);
}
